import { FlowApprovalTypeSchema, type FlowApprovalType } from '@reflex/flows';
import { selectableActions, type FlowActionCatalogEntry } from './action-catalog.js';
import { eventKey, type FlowEventCatalogEntry } from './event-catalog.js';

/**
 * Specification kinds offered in the "Add Trigger, Connectors, and Specs"
 * picker on the create-automation landing. Triggers and actions come from
 * the org's catalogs; the rest are hand-authored node-type hints for the
 * generate prompt (they have no per-connector catalog entry).
 */
export const SPEC_KINDS = ['trigger', 'action', 'agent_task', 'filter', 'approval'] as const;
export type SpecKind = (typeof SPEC_KINDS)[number];

/** Chip label per kind. `Record`-typed so a new kind fails to compile here. */
export const SPEC_KIND_LABELS: Record<SpecKind, string> = {
  trigger: 'Triggers',
  action: 'Actions',
  agent_task: 'Agent Task',
  filter: 'Filters',
  approval: 'Human Approval',
};

/**
 * One selectable specification. Connector-scoped for triggers/actions;
 * `connector: null` for the generic node-type hints.
 */
export interface Specification {
  /** Stable selection key, e.g. `trigger:github/issue`, `approval:pr_review`. */
  id: string;
  kind: SpecKind;
  label: string;
  description: string;
  /** Plugin name for connector-scoped specs; `null` for generic node types. */
  connector: string | null;
}

/** Human labels + descriptions for each approval type in the schema. */
export const APPROVAL_TYPE_COPY: Record<FlowApprovalType, { label: string; description: string }> =
  {
    understanding_review: {
      label: 'Understanding review',
      description: 'Human gate that confirms the agent understood the request before acting',
    },
    plan_review: {
      label: 'Plan review',
      description: 'Human gate that reviews a proposed plan before implementation',
    },
    pr_review: {
      label: 'PR review',
      description: 'Human gate that reviews a pull request before it proceeds',
    },
    generic: {
      label: 'Generic approval',
      description: 'Human approval gate with a custom prompt',
    },
  };

/**
 * Generic (connector-independent) specifications: one Agent Task, one Filter,
 * and one entry per {@link FlowApprovalTypeSchema} option. Approval entries
 * are derived from the schema's options so the list cannot drift from
 * `@reflex/flows`.
 */
export const GENERIC_SPECIFICATIONS: Specification[] = [
  {
    id: 'agent_task:default',
    kind: 'agent_task',
    label: 'Agent Task',
    description: 'Agent with structured output (text or JSON) and optional schema validation',
    connector: null,
  },
  {
    id: 'filter:default',
    kind: 'filter',
    label: 'Filter',
    description: 'Continue only when conditions on upstream data match',
    connector: null,
  },
  ...FlowApprovalTypeSchema.options.map(
    (approvalType): Specification => ({
      id: `approval:${approvalType}`,
      kind: 'approval',
      label: APPROVAL_TYPE_COPY[approvalType].label,
      description: APPROVAL_TYPE_COPY[approvalType].description,
      connector: null,
    }),
  ),
];

/** Map catalog entries into a flat specification list (events + actions + generics). */
export function buildSpecifications(input: {
  events: FlowEventCatalogEntry[];
  actions: FlowActionCatalogEntry[];
}): Specification[] {
  const triggers: Specification[] = input.events.map((entry) => ({
    id: `trigger:${eventKey(entry)}`,
    kind: 'trigger',
    label: humanizeEventLabel(entry),
    description: entry.description || `Triggers when a ${entry.event} event is delivered`,
    connector: entry.plugin,
  }));

  const actions: Specification[] = selectableActions(input.actions).map((entry) => ({
    id: `action:${entry.provider}:${entry.operation}`,
    kind: 'action',
    label: humanizeActionLabel(entry),
    description: entry.description ?? entry.operation,
    connector: entry.pluginName ?? entry.provider,
  }));

  return [...triggers, ...actions, ...GENERIC_SPECIFICATIONS];
}

export interface SpecificationFilter {
  /** Restrict to one connector; generics (`connector: null`) are excluded. */
  connector?: string | null;
  /** Restrict to these kinds; empty / omitted means every kind. */
  kinds?: ReadonlySet<SpecKind> | SpecKind[];
  /** Case-insensitive match against label + description. */
  search?: string;
}

/** Filter a specification list by connector, kind chips, and search text. */
export function filterSpecifications(
  specs: Specification[],
  filter: SpecificationFilter = {},
): Specification[] {
  const kinds =
    filter.kinds == null
      ? null
      : filter.kinds instanceof Set
        ? filter.kinds
        : new Set(filter.kinds);
  const search = filter.search?.trim().toLowerCase() ?? '';

  return specs.filter((spec) => {
    if (filter.connector) {
      if (spec.connector !== filter.connector) return false;
    }
    if (kinds && kinds.size > 0 && !kinds.has(spec.kind)) return false;
    if (search) {
      const haystack = `${spec.label} ${spec.description}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

/** Singular kind label used in the generate-prompt suffix. */
const SPEC_KIND_PROMPT_LABELS: Record<SpecKind, string> = {
  trigger: 'Trigger',
  action: 'Action',
  agent_task: 'Agent Task',
  filter: 'Filter',
  approval: 'Human Approval',
};

/**
 * Serialize selected specifications into a prompt suffix for AI generation.
 * Returns `''` when nothing is selected so the prompt is left untouched.
 */
export function specificationsPromptSuffix(specs: Specification[]): string {
  if (specs.length === 0) return '';
  const lines = specs.map(
    (spec) => `- ${SPEC_KIND_PROMPT_LABELS[spec.kind]}: ${spec.label} - ${spec.description}`,
  );
  return `\n\nRequired specifications:\n${lines.join('\n')}`;
}

/** Unique connector names present on connector-scoped specs, sorted. */
export function connectorsFromSpecifications(specs: Specification[]): string[] {
  const set = new Set<string>();
  for (const spec of specs) {
    if (spec.connector) set.add(spec.connector);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/**
 * Group filtered specs for the results list. Headers read like
 * `GITHUB · TRIGGERS`; generics use only the kind label.
 */
export function groupSpecifications(
  specs: Specification[],
): Array<{ key: string; heading: string; entries: Specification[] }> {
  const groups = new Map<string, { heading: string; entries: Specification[] }>();
  for (const spec of specs) {
    const key = spec.connector ? `${spec.connector}:${spec.kind}` : spec.kind;
    const existing = groups.get(key);
    if (existing) {
      existing.entries.push(spec);
      continue;
    }
    const kindLabel = SPEC_KIND_LABELS[spec.kind].toUpperCase();
    const heading = spec.connector ? `${spec.connector.toUpperCase()} · ${kindLabel}` : kindLabel;
    groups.set(key, { heading, entries: [spec] });
  }
  return [...groups.entries()].map(([key, group]) => ({ key, ...group }));
}

function humanizeEventLabel(entry: FlowEventCatalogEntry): string {
  // Prefer a readable title from the event id: `github_issue` → `Github issue`.
  const raw = entry.event.replace(/_/g, ' ');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function humanizeActionLabel(entry: FlowActionCatalogEntry): string {
  const raw = entry.operation.replace(/_/g, ' ');
  return `${entry.provider.charAt(0).toUpperCase()}${entry.provider.slice(1)} ${raw}`;
}
